import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  BOARD_THEME_IDS,
  BOARD_THEMES,
  type BoardThemeId,
} from 'react-chess-core';
import {
  settingsBoardThemeLabelSx,
  settingsBoardThemeOptionSx,
  settingsBoardThemeSwatchSx,
} from './settingsMuiStyles';

type BoardThemePickerProps = {
  value: BoardThemeId;
  disabled?: boolean;
  onChange: (value: BoardThemeId) => void;
};

const BoardThemeSwatch = ({ boardTheme }: { boardTheme: BoardThemeId }) => {
  const { darkSquare, lightSquare } = BOARD_THEMES[boardTheme];

  return (
    <Box sx={settingsBoardThemeSwatchSx} aria-hidden>
      <Box sx={{ bgcolor: lightSquare }} />
      <Box sx={{ bgcolor: darkSquare }} />
      <Box sx={{ bgcolor: darkSquare }} />
      <Box sx={{ bgcolor: lightSquare }} />
    </Box>
  );
};

export const BoardThemePicker = ({
  value,
  disabled,
  onChange,
}: BoardThemePickerProps) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: {
        xs: 'repeat(2, minmax(0, 1fr))',
        sm: 'repeat(3, minmax(0, 1fr))',
      },
      gap: { xs: 1.5, xxl: 1.75, xxxl: 2 },
    }}
  >
    {BOARD_THEME_IDS.map((boardTheme) => {
      const selected = boardTheme === value;
      const { label } = BOARD_THEMES[boardTheme];

      return (
        <Box
          key={boardTheme}
          component="button"
          type="button"
          disabled={disabled}
          onClick={() => onChange(boardTheme)}
          aria-pressed={selected}
          sx={{
            ...settingsBoardThemeOptionSx,
            borderColor: selected ? 'primary.main' : 'divider',
            bgcolor: selected ? 'action.selected' : 'background.paper',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1,
            '&:hover': disabled
              ? undefined
              : {
                  borderColor: selected ? 'primary.main' : 'text.secondary',
                  bgcolor: 'action.hover',
                },
          }}
        >
          <BoardThemeSwatch boardTheme={boardTheme} />
          <Typography
            variant="body2"
            fontWeight={selected ? 600 : 400}
            sx={settingsBoardThemeLabelSx}
          >
            {label}
          </Typography>
        </Box>
      );
    })}
  </Box>
);
